import { useEffect, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { api, formatApiError } from "@/lib/api";
import { CandidateCard, CandidateDetailModal } from "@/components/CandidateCard";
import { toast } from "sonner";
import { Loader2, Sparkles, Users, Briefcase, ShieldCheck, X } from "lucide-react";

export default function RecruiterNominationsAdmin() {
  const [nominations, setNominations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/recruiter-nominations");
      setNominations(data.nominations || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openCount = nominations.filter((n) => n.opportunity).length;

  return (
    <DashboardLayout>
      <div data-testid="recruiter-nominations-admin">
        <div className="mb-6">
          <div className="text-xs font-bold uppercase tracking-widest text-teal-700">لوحة النظام</div>
          <h1 className="mt-2 text-2xl font-heading font-black text-slate-900">ترشيحات جهات الاستقطاب</h1>
          <p className="mt-2 text-slate-600 max-w-3xl">
            هذه هي البطاقات نفسها التي تراها جهات الاستقطاب. المنصّة (النظام) هي من ترشّح الطلبة تلقائياً لجهات الاستقطاب بناءً على الفرص المفتوحة، تقييمات الجمعية والموجّه، والجدارات المكتسبة. عند فتح فرصة جديدة أو تحديث بيانات الخريجين، تُحدَّث هذه القائمة تلقائياً.
          </p>
        </div>

        <div className="grid gap-4 sm:grid-cols-3 mb-8">
          <StatCard icon={Users} label="عدد المرشّحين النشطين" value={nominations.length} tint="text-teal-700 bg-teal-50 border-teal-100" />
          <StatCard icon={Briefcase} label="فرص مرتبطة" value={openCount} tint="text-amber-700 bg-amber-50 border-amber-100" />
          <StatCard icon={ShieldCheck} label="مصدر البيانات" value="النظام" tint="text-emerald-700 bg-emerald-50 border-emerald-100" />
        </div>

        {loading ? (
          <div className="grid place-items-center py-16"><Loader2 className="h-8 w-8 animate-spin text-teal-700" /></div>
        ) : nominations.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center text-slate-600">
            <Sparkles className="h-8 w-8 text-teal-700 mx-auto mb-3" />
            لا توجد ترشيحات نشطة حالياً — سيتم توليدها تلقائياً عند وجود فرص منشورة وخريجين معتمدين.
          </div>
        ) : (
          <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3" data-testid="admin-nominations-grid">
            {nominations.map(({ student, opportunity }) => (
              <CandidateCard
                key={student.id}
                student={student}
                opportunity={opportunity}
                onOpen={(s, o) => setDetail({ student: s, opportunity: o })}
              />
            ))}
          </div>
        )}

        <CandidateDetailModal
          payload={detail}
          onClose={() => setDetail(null)}
          actionSlot={
            <>
              <div className="flex-1 text-xs text-slate-500 flex items-center gap-2">
                <ShieldCheck className="h-4 w-4 text-emerald-600" />
                عرض للنظام فقط — لا تحتوي هذه النافذة على أي إجراء تواصل مباشر.
              </div>
              <button onClick={() => setDetail(null)} className="px-4 py-3 rounded-lg text-slate-700 hover:bg-slate-100 inline-flex items-center gap-2">
                <X className="h-4 w-4" /> إغلاق
              </button>
            </>
          }
        />
      </div>
    </DashboardLayout>
  );
}

function StatCard({ icon: Icon, label, value, tint }) {
  return (
    <div className={`rounded-2xl border p-4 flex items-center gap-3 ${tint}`}>
      <div className="h-10 w-10 rounded-xl bg-white grid place-items-center shadow-sm">
        <Icon className="h-5 w-5" />
      </div>
      <div>
        <div className="text-[11px] font-bold uppercase tracking-widest opacity-80">{label}</div>
        <div className="font-heading font-black text-lg arabic-numerals">{value}</div>
      </div>
    </div>
  );
}
